/**
 * Success Page
 * Booking confirmation success
 */

import { t, formatDate, formatCurrency, getCurrentLanguage, type Language } from '../i18n';
import { getState, navigateTo } from '../store';
import { closeWindow, sendMessage, isInLineApp } from '../liff';

/**
 * Render success page
 */
export function renderSuccessPage(): string {
  const state = getState();
  const { booking } = state;

  if (!booking) {
    return '';
  }

  const channel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || 'line';
  const isLine = channel === 'line';
  const isWhatsApp = channel === 'whatsapp';
  const isWeChat = channel === 'wechat';
  const isWeb = channel === 'web';

  let chatMessage = t('confirmationSentToChat') || 'ส่งข้อมูลยืนยันไปยังห้องแชท LINE แล้ว ระบบกำลังนำท่านกลับสู่แชท...';
  if (isWhatsApp) {
    chatMessage = t('returnToWhatsAppChat') || 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว แตะปุ่มด้านล่างเพื่อกลับสู่ห้องแชทครับ';
  } else if (isWeChat) {
    chatMessage = t('returnToWeChatChat') || '预订已确认，请点击下方按钮关闭页面返回微信。';
  } else if (isWeb) {
    chatMessage = t('returnToChatWeb') || 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว แตะปุ่มด้านล่างเพื่อปิดหน้านี้และกลับสู่หน้าเว็บแชทครับ';
  }

  return `
    <div class="success-page" style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 80vh; padding: 24px 20px; text-align: center;">
      <div class="success-icon" style="width: 80px; height: 80px; line-height: 80px; font-size: 40px; border-radius: 50%; background: #E8F5E9; color: #00B900; margin-bottom: 20px; box-shadow: 0 4px 16px rgba(0, 185, 0, 0.25); display: flex; align-items: center; justify-content: center;">✓</div>
      
      <h1 class="success-title" style="font-size: 24px; font-weight: 700; color: #111; margin-bottom: 8px;">${t('successTitle') || 'จองสำเร็จ!'}</h1>
      <p class="success-subtitle" style="font-size: 15px; color: #555; margin-bottom: 24px;">${t('bookingConfirmed') || 'การจองของคุณได้รับการยืนยันเรียบร้อยแล้ว'}</p>
      
      <div class="confirmation-box" style="background: #f0faf0; border: 1px solid #c8e6c9; border-radius: 14px; padding: 16px 24px; margin-bottom: 24px; width: 100%; max-width: 320px;">
        <div class="confirmation-label" style="font-size: 12px; color: #2e7d32; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 6px;">${t('confirmationNumber') || 'หมายเลขยืนยัน'}</div>
        <div class="confirmation-number" style="font-size: 22px; font-weight: 800; color: #00B900; letter-spacing: 1.5px;">${booking.confirmationNumber || booking.id || '-'}</div>
      </div>

      <div style="background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 10px; padding: 12px 18px; margin-bottom: 28px; font-size: 13px; color: #495057; display: flex; align-items: center; gap: 10px; max-width: 320px; text-align: left;">
        <span style="font-size: 18px;">💬</span>
        <span>${chatMessage}</span>
      </div>

      ${isLine ? '<div class="spinner" style="width: 28px; height: 28px; border: 3px solid #e0e0e0; border-top: 3px solid #00B900; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 24px;"></div>' : ''}

      <div class="success-actions" style="width: 100%; max-width: 320px; display: flex; flex-direction: column; gap: 10px;">
        ${isWhatsApp ? `
          <a 
            href="https://wa.me/15556643267" 
            class="btn btn-primary btn-block"
            style="padding: 14px; font-size: 15px; border-radius: 10px; background: #25D366; color: #fff; text-decoration: none; display: flex; align-items: center; justify-content: center; font-weight: 600; box-shadow: 0 4px 14px rgba(37,211,102,0.35); gap: 8px;"
          >
            <span>💬</span>
            <span>${t('returnToWhatsAppBtn')}</span>
          </a>
        ` : isWeChat ? `
          <button 
            type="button" 
            class="btn btn-primary btn-block"
            onclick="window.closeSuccessPage()"
            style="padding: 14px; font-size: 15px; border-radius: 10px; background: #07C160; color: #fff; border: none; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 4px 14px rgba(7,193,96,0.35);"
          >
            <span>💬</span>
            <span>${t('returnToWeChatBtn')}</span>
          </button>
        ` : `
          <button 
            type="button" 
            class="btn btn-primary btn-block"
            onclick="window.closeSuccessPage()"
            style="padding: 14px; font-size: 15px; border-radius: 10px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 8px;"
          >
            <span>✕</span>
            <span>${t('closeWindowBtn')}</span>
          </button>
        `}
      </div>
    </div>
  `;
}

// Set to track sent confirmation numbers in memory (prevents double send in same session)
export const sentConfirmationNumbers = new Set<string>();

export function resetSentConfirmation(confNum?: string): void {
  if (confNum) {
    sentConfirmationNumbers.delete(confNum);
    try {
      sessionStorage.removeItem(`sent_flex_${confNum}`);
    } catch (_) {}
  }
}

/**
 * Build LINE Flex Message for booking confirmation (Multi-language & Modular)
 */
function buildSuccessFlexMessage(booking: any) {
  const lang = (((booking.language || getCurrentLanguage() || 'th') as string).toLowerCase() as Language) || 'th';
  const isPaid = booking.paymentStatus === 'paid';

  const titles: Record<Language, {
    title: string;
    subtitle: string;
    confNum: string;
    roomType: string;
    ratePlan: string;
    refundPolicy: string;
    nonRefundable: string;
    refundable: string;
    guest: string;
    checkIn: string;
    checkOut: string;
    serviceDate: string;
    timeSlot: string;
    nights: string;
    rooms: string;
    guests: string;
    adults: string;
    children: string;
    roomRateBeforeTax: string;
    serviceRateBeforeTax: string;
    extraCharges: string;
    serviceCharge10: string;
    vat7: string;
    total: string;
    payment: string;
    paid: string;
    payAtHotel: string;
    payAtSpa: string;
    payAtRestaurant: string;
    spaService: string;
    spaTreatment: string;
    spaDuration: string;
    spaNotice: string;
    diningService: string;
    diningOutlet: string;
    diningSeating: string;
    diningNotice: string;
    miceTitle: string;
    miceSubtitle: string;
    miceService: string;
    miceRoom: string;
    miceLayout: string;
    micePackage: string;
    miceCompany: string;
    miceNotice: string;
    quotationRequested: string;
    payAtVenue: string;
    roomNumber: string;
    checkInTime: string;
    checkOutTime: string;
    seminarNotice: string;
    seminarDeskNotice: string;
    genericNotice: string;
    genericDeskNotice: string;
  }> = {
    th: {
      title: 'จองสำเร็จ!',
      subtitle: 'การจองของคุณได้รับการยืนยันแล้ว',
      confNum: 'หมายเลขยืนยัน',
      roomType: 'ประเภทห้อง',
      ratePlan: 'แผนราคา',
      refundPolicy: 'นโยบายการคืนเงิน',
      nonRefundable: 'ไม่สามารถคืนเงินได้',
      refundable: 'ยกเลิกฟรีตามเงื่อนไข',
      guest: 'ชื่อผู้จอง / ผู้เข้าพัก',
      checkIn: 'วันเช็คอิน',
      checkOut: 'วันเช็คเอาท์',
      serviceDate: 'วันที่รับบริการ',
      timeSlot: 'รอบเวลา',
      nights: 'คืน',
      rooms: 'ห้อง',
      guests: 'ผู้เข้าพัก',
      adults: 'ผู้ใหญ่',
      children: 'เด็ก',
      roomRateBeforeTax: 'ค่าห้อง (ก่อนภาษี)',
      serviceRateBeforeTax: 'ค่าบริการ (ก่อนภาษี)',
      extraCharges: 'ค่าใช้จ่ายเพิ่มเติม',
      serviceCharge10: 'ค่าบริการ 10%',
      vat7: 'VAT 7%',
      total: 'ยอดรวมทั้งหมด',
      payment: 'การชำระเงิน',
      paid: 'ชำระเงินออนไลน์แล้ว',
      payAtHotel: 'ชำระที่โรงแรม (Pay at Hotel)',
      payAtSpa: 'ชำระเงินที่ร้าน (Pay at Spa)',
      payAtRestaurant: 'ชำระเงินที่ร้าน (Pay at Restaurant)',
      spaService: 'สปา',
      spaTreatment: 'ทรีตเมนต์',
      spaDuration: 'นาที',
      spaNotice: 'กรุณามาถึงก่อนเวลานัดหมาย 15 นาที',
      diningService: 'ห้องอาหาร',
      diningOutlet: 'ห้องอาหาร',
      diningSeating: 'โซนที่นั่ง',
      diningNotice: 'โต๊ะอาหารจะถูกสำรองไว้ 15 นาทีจากเวลานัดหมาย',
      miceTitle: 'จองห้องประชุมและจัดเลี้ยงสำเร็จ!',
      miceSubtitle: 'การจองสถานที่ได้รับการยืนยันเรียบร้อยแล้ว',
      miceService: 'ประชุมและจัดเลี้ยง (MICE)',
      miceRoom: 'สถานที่ / ห้องจัดงาน',
      miceLayout: 'รูปแบบจัดโต๊ะ',
      micePackage: 'แพ็กเกจการจัดงาน',
      miceCompany: 'บริษัท / องค์กร',
      miceNotice: 'เจ้าหน้าที่ฝ่ายขายจะติดต่อกลับเพื่อยืนยันรายละเอียดงานและอุปกรณ์เพิ่มเติม',
      quotationRequested: 'ขอใบเสนอราคา (RFP)',
      payAtVenue: 'ชำระ ณ สถานที่ (Pay at Venue)',
      roomNumber: 'หมายเลขห้องพัก',
      checkInTime: 'เวลาเช็คอิน: 14:00 น.',
      checkOutTime: 'เวลาเช็คเอาท์: 12:00 น.',
      seminarNotice: 'กรุณามาถึงก่อนเวลาเริ่มสัมมนา 15-30 นาที เพื่อลงทะเบียนรับเอกสาร',
      seminarDeskNotice: 'กรุณานำหมายเลขยืนยันนี้แสดง ณ จุดลงทะเบียน',
      genericNotice: 'กรุณามาถึงก่อนเวลานัดหมาย 10-15 นาที',
      genericDeskNotice: 'กรุณาแสดงหมายเลขยืนยันนี้เมื่อเข้ารับบริการ',
    },
    en: {
      title: 'Booking Confirmed!',
      subtitle: 'Your booking has been confirmed',
      confNum: 'Confirmation Number',
      roomType: 'Room Type',
      ratePlan: 'Rate Plan',
      refundPolicy: 'Refund Policy',
      nonRefundable: 'Non-Refundable',
      refundable: 'Free Cancellation',
      guest: 'Guest Name',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      serviceDate: 'Service Date',
      timeSlot: 'Time Slot',
      nights: 'Night(s)',
      rooms: 'Room(s)',
      guests: 'Guests',
      adults: 'Adult(s)',
      children: 'Child(ren)',
      roomRateBeforeTax: 'Room Rate (Before Tax)',
      serviceRateBeforeTax: 'Service Rate (Before Tax)',
      extraCharges: 'Extra Charges',
      serviceCharge10: 'Service Charge 10%',
      vat7: 'VAT 7%',
      total: 'Total Amount',
      payment: 'Payment',
      paid: 'Paid Online',
      payAtHotel: 'Pay at Hotel',
      payAtSpa: 'Pay at Spa',
      payAtRestaurant: 'Pay at Restaurant',
      spaService: 'Spa',
      spaTreatment: 'Treatment',
      spaDuration: 'mins',
      spaNotice: 'Please arrive 15 minutes prior to your appointment',
      diningService: 'Restaurant',
      diningOutlet: 'Restaurant',
      diningSeating: 'Seating Area',
      diningNotice: 'Table will be held for 15 minutes past reservation time',
      miceTitle: 'MICE & Event Confirmed!',
      miceSubtitle: 'Your event reservation has been confirmed',
      miceService: 'Meetings & Events (MICE)',
      miceRoom: 'Function Room / Space',
      miceLayout: 'Seating Layout',
      micePackage: 'Event Package',
      miceCompany: 'Company / Organization',
      miceNotice: 'Our event specialist will contact you shortly to finalize details and AV setup',
      quotationRequested: 'Quotation on Request (RFP)',
      payAtVenue: 'Pay at Venue',
      roomNumber: 'Room Number',
      checkInTime: 'Check-in Time: 2:00 PM',
      checkOutTime: 'Check-out Time: 12:00 PM',
      seminarNotice: 'Please arrive 15-30 minutes before the seminar begins for registration',
      seminarDeskNotice: 'Please present this confirmation number at the registration desk',
      genericNotice: 'Please arrive 10-15 minutes prior to your appointment',
      genericDeskNotice: 'Please present this confirmation upon arrival',
    },
    zh: {
      title: '预订成功！',
      subtitle: '您的预订已确认',
      confNum: '预订编号',
      roomType: '房型',
      ratePlan: '价格计划',
      refundPolicy: '退款政策',
      nonRefundable: '不可退款',
      refundable: '免费取消',
      guest: '入住人 / 客人',
      checkIn: '入住日期',
      checkOut: '离店日期',
      serviceDate: '服务日期',
      timeSlot: '时间段',
      nights: '晚',
      rooms: '间',
      guests: '客人',
      adults: '成人',
      children: '儿童',
      roomRateBeforeTax: '房费（税前）',
      serviceRateBeforeTax: '服务费（税前）',
      extraCharges: '额外费用',
      serviceCharge10: '服务费 10%',
      vat7: 'VAT 7%',
      total: '总金额',
      payment: '支付方式',
      paid: '在线已支付',
      payAtHotel: '到店支付 (Pay at Hotel)',
      payAtSpa: '到店支付 (Pay at Spa)',
      payAtRestaurant: '到店支付 (Pay at Restaurant)',
      spaService: '水疗中心',
      spaTreatment: '护理项目',
      spaDuration: '分钟',
      spaNotice: '请提前15分钟到达',
      diningService: '餐厅',
      diningOutlet: '餐厅',
      diningSeating: '用餐区域',
      diningNotice: '预订座位将保留15分钟',
      miceTitle: '会议与宴会预订成功！',
      miceSubtitle: '您的活动场地预订已确认',
      miceService: '会议与宴会 (MICE)',
      miceRoom: '会议场地 / 宴会厅',
      miceLayout: '摆台形式',
      micePackage: '会议套餐',
      miceCompany: '公司 / 机构',
      miceNotice: '宴会专员将尽快与您联系以确认细节与视听设备需求',
      quotationRequested: '定制报价申请 (RFP)',
      payAtVenue: '到店结算 (Pay at Venue)',
      roomNumber: '房间号',
      checkInTime: '入住时间: 14:00',
      checkOutTime: '退房时间: 12:00',
      seminarNotice: '请在研讨会开始前15-30分钟到达现场签到领取资料',
      seminarDeskNotice: '请在签到处出示此确认编号',
      genericNotice: '请在预约时间前10-15分钟到达',
      genericDeskNotice: '请在接受服务时出示此确认信息',
    },
    ru: {
      title: 'Бронирование подтверждено!',
      subtitle: 'Ваше бронирование успешно подтверждено',
      confNum: 'Номер бронирования',
      roomType: 'Категория номера',
      ratePlan: 'Тарифный план',
      refundPolicy: 'Правила отмены',
      nonRefundable: 'Без возврата средств',
      refundable: 'Бесплатная отмена',
      guest: 'Имя гостя',
      checkIn: 'Заезд',
      checkOut: 'Выезд',
      serviceDate: 'Дата услуги',
      timeSlot: 'Время сеанса',
      nights: 'Ночей',
      rooms: 'Номеров',
      guests: 'Гости',
      adults: 'Взрослых',
      children: 'Детей',
      roomRateBeforeTax: 'Стоимость номера (без налогов)',
      serviceRateBeforeTax: 'Стоимость услуги (без налогов)',
      extraCharges: 'Дополнительные сборы',
      serviceCharge10: 'Сервисный сбор 10%',
      vat7: 'НДС 7%',
      total: 'Итоговая сумма',
      payment: 'Оплата',
      paid: 'Оплачено онлайн',
      payAtHotel: 'Оплата в отеле (Pay at Hotel)',
      payAtSpa: 'Оплата в СПА (Pay at Spa)',
      payAtRestaurant: 'Оплата в ресторане (Pay at Restaurant)',
      spaService: 'СПА-центр',
      spaTreatment: 'Процедура',
      spaDuration: 'мин',
      spaNotice: 'Пожалуйста, приходите за 15 минут до назначенного времени',
      diningService: 'Бронирование столика',
      diningOutlet: 'Ресторан',
      diningSeating: 'Зона посадки',
      diningNotice: 'Бронь столика сохраняется в течение 15 минут',
      miceTitle: 'Мероприятие подтверждено!',
      miceSubtitle: 'Бронирование площадки успешно подтверждено',
      miceService: 'Мероприятия и банкеты (MICE)',
      miceRoom: 'Площадка / Конференц-зал',
      miceLayout: 'Схема рассадки',
      micePackage: 'Пакет мероприятия',
      miceCompany: 'Компания / Организация',
      miceNotice: 'Наш менеджер свяжется с вами в ближайшее время для уточнения деталей',
      quotationRequested: 'Запрос ценового предложения (RFP)',
      payAtVenue: 'Оплата на месте (Pay at Venue)',
      roomNumber: 'Номер комнаты',
      checkInTime: 'Время заезда: 14:00',
      checkOutTime: 'Время выезда: 12:00',
      seminarNotice: 'Пожалуйста, приходите за 15-30 минут до начала для регистрации',
      seminarDeskNotice: 'Пожалуйста, предъявите этот номер бронирования на стойке регистрации',
      genericNotice: 'Пожалуйста, приходите за 10-15 минут до назначенного времени',
      genericDeskNotice: 'Пожалуйста, предъявите это подтверждение при посещении',
    },
    ja: {
      title: 'ご予約完了！',
      subtitle: 'ご予約が正常に完了いたしました',
      confNum: '予約番号',
      roomType: 'お部屋タイプ',
      ratePlan: '料金プラン',
      refundPolicy: 'キャンセル規定',
      nonRefundable: '返金不可',
      refundable: '無料キャンセル可能',
      guest: 'ご宿泊者名',
      checkIn: 'チェックイン',
      checkOut: 'チェックアウト',
      serviceDate: 'ご利用日',
      timeSlot: 'ご利用時間',
      nights: '泊',
      rooms: '部屋',
      guests: '宿泊者数',
      adults: '大人',
      children: '子供',
      roomRateBeforeTax: '客室料金（税抜）',
      serviceRateBeforeTax: 'サービス料金（税抜）',
      extraCharges: '追加料金',
      serviceCharge10: 'サービス料 10%',
      vat7: 'VAT（付加価値税）7%',
      total: '合計金額',
      payment: 'お支払い方法',
      paid: 'オンライン決済完了',
      payAtHotel: '現地払い (Pay at Hotel)',
      payAtSpa: '店頭払い (Pay at Spa)',
      payAtRestaurant: '店頭払い (Pay at Restaurant)',
      spaService: 'スパ',
      spaTreatment: 'トリートメント',
      spaDuration: '分',
      spaNotice: 'ご予約時間の15分前までにお越しください',
      diningService: 'レストラン予約',
      diningOutlet: 'レストラン',
      diningSeating: 'お席エリア',
      diningNotice: 'お席はご予約時間より15分間確保いたします',
      miceTitle: '会議・イベント予約完了！',
      miceSubtitle: '会場のご予約が確認されました',
      miceService: '会議・宴会 (MICE)',
      miceRoom: '会場・会議室',
      miceLayout: 'レイアウト',
      micePackage: 'イベントパッケージ',
      miceCompany: '企業・団体名',
      miceNotice: '担当プランナーより詳細確認のため折り返しご連絡いたします',
      quotationRequested: 'お見積もりリクエスト (RFP)',
      payAtVenue: '現地決済 (Pay at Venue)',
      roomNumber: 'お部屋番号',
      checkInTime: 'チェックイン時間: 14:00',
      checkOutTime: 'チェックアウト時間: 12:00',
      seminarNotice: '受付・資料受取のため、開始15〜30分前にお越しください',
      seminarDeskNotice: '受付にてこの予約番号をご提示ください',
      genericNotice: 'ご予約時間の10〜15分前までにお越しください',
      genericDeskNotice: 'ご来店時にこの確認画面をご提示ください',
    },
    ko: {
      title: '예약 완료!',
      subtitle: '고객님의 예약이 성공적으로 확정되었습니다',
      confNum: '예약 번호',
      roomType: '객실 타입',
      ratePlan: '요금 플랜',
      refundPolicy: '취소/환불 규정',
      nonRefundable: '환불 불가',
      refundable: '무료 취소 가능',
      guest: '투숙객 성명',
      checkIn: '체크인',
      checkOut: '체크아웃',
      serviceDate: '이용 일자',
      timeSlot: '이용 시간',
      nights: '박',
      rooms: '개실',
      guests: '투숙 인원',
      adults: '성인',
      children: '어린이',
      roomRateBeforeTax: '객실 요금 (세전)',
      serviceRateBeforeTax: '서비스 요금 (세전)',
      extraCharges: '추가 요금',
      serviceCharge10: '서비스 요금 10%',
      vat7: '부가세 7%',
      total: '총 결제 금액',
      payment: '결제 방식',
      paid: '온라인 결제 완료',
      payAtHotel: '현장 결제 (Pay at Hotel)',
      payAtSpa: '매장 결제 (Pay at Spa)',
      payAtRestaurant: '매장 결제 (Pay at Restaurant)',
      spaService: '스파',
      spaTreatment: '트리트먼트',
      spaDuration: '분',
      spaNotice: '예약 시간 15분 전까지 도착해 주시기 바랍니다',
      diningService: '레스토랑 예약',
      diningOutlet: '레스토랑',
      diningSeating: '좌석 구역',
      diningNotice: '예약 시간 기준 15분간 좌석이 유지됩니다',
      miceTitle: 'MICE 및 행사 예약 완료!',
      miceSubtitle: '행사장 예약이 성공적으로 확정되었습니다',
      miceService: '회의 및 연회 (MICE)',
      miceRoom: '행사장 / 연회장',
      miceLayout: '좌석 배치',
      micePackage: '행사 패키지',
      miceCompany: '회사 / 단체명',
      miceNotice: '담당자가 상세 일정 및 필요 장비 조율을 위해 곧 연락드리겠습니다',
      quotationRequested: '견적 요청 (RFP)',
      payAtVenue: '현장 결제 (Pay at Venue)',
      roomNumber: '객실 번호',
      checkInTime: '체크인 시간: 14:00',
      checkOutTime: '체크아웃 시간: 12:00',
      seminarNotice: '등록 및 자료 수령을 위해 시작 15~30분 전에 도착해 주시기 바랍니다',
      seminarDeskNotice: '등록 데스크에서 본 확인 번호를 제시해 주세요',
      genericNotice: '예약 시간 10~15분 전까지 도착해 주시기 바랍니다',
      genericDeskNotice: '방문 시 본 확인 메시지를 제시해 주세요',
    },
  };

  const isRfp = booking.bookingType === 'RFP' ||
    booking.engine === 'request-rfp' ||
    booking.engine === 'request_rfp' ||
    booking.serviceId === 'member_services';

  const isSeminar = (booking.bookingType === 'COURSE' ||
    booking.resourceType === 'course' ||
    booking.resourceType === 'class' ||
    booking.resourceType === 'workshop' ||
    String(booking.resourceName || '').includes('CPD') ||
    String(booking.roomTypeName || '').includes('CPD') ||
    String(booking.itemName || '').includes('CPD') ||
    String(booking.resourceName || '').includes('สัมมนา') ||
    String(booking.roomTypeName || '').includes('สัมมนา') ||
    String(booking.itemName || '').includes('สัมมนา') ||
    String(booking.resourceName || '').includes('อบรม') ||
    String(booking.roomTypeName || '').includes('อบรม') ||
    String(booking.itemName || '').includes('อบรม')) && !isRfp;

  const isAppointment = (booking.bookingType === 'APPOINTMENT' ||
    booking.engine === 'appointment' ||
    booking.bookingType === 'TICKET') && !isRfp && !isSeminar;

  const isSpa = (booking.bookingType === 'SPA' || Boolean(booking.spaDetails)) && !isAppointment && !isSeminar && !isRfp;
  const isRestaurant = (booking.bookingType === 'RESTAURANT' || Boolean(booking.restaurantDetails)) && !isAppointment && !isSeminar && !isRfp;
  const isMice = (booking.bookingType === 'MICE' ||
    booking.bookingType === 'EVENT' ||
    booking.resourceType === 'facility' ||
    booking.customFields?.bookingType === 'MICE' ||
    String(booking.confirmationNumber || '').startsWith('MICE-') ||
    String(booking.id || '').startsWith('MICE-') ||
    (Boolean(booking.miceDetails || booking.customFields?.miceDetails) && !isRfp)) && !isAppointment && !isSeminar && !isRfp;

  const isGenericEngine = isAppointment ||
    isSeminar ||
    isRfp ||
    Boolean(booking.isGenericEngine) ||
    ['APPOINTMENT', 'ORDER', 'TICKET', 'FACILITY', 'COURSE', 'CLASS', 'EVENT', 'SERVICE', 'RFP'].includes(String(booking.bookingType || '').toUpperCase()) ||
    Boolean(booking.resourceName || booking.itemName || booking.courseName);

  const tDict = titles[lang] || titles.th;
  const cardTitle = isRfp
    ? (lang === 'th' ? 'ยืนยันการส่งคำขอ' : 'Request Confirmation')
    : isSeminar
    ? (lang === 'th' ? 'ยืนยันการลงทะเบียน' : 'Registration Confirmed')
    : isMice ? tDict.miceTitle : tDict.title;

  const cardSubtitle = isRfp
    ? (lang === 'th' ? 'คำขอรับบริการ / ขอใบเสนอราคา' : 'Service Request / Quotation')
    : isSeminar
    ? (lang === 'th' ? 'ลงทะเบียนสัมมนา / อบรมสำเร็จ' : 'Course & Seminar Confirmed')
    : isMice ? tDict.miceSubtitle : tDict.subtitle;

  const guestName = booking.guestName || `${booking.guest?.firstName || ''} ${booking.guest?.lastName || ''}`.trim() || 'Valued Guest';
  const totalAmount = Number(booking.totalAmount || booking.totalRate || 0);
  const currency = booking.currency || 'THB';

  const cardBrand = booking.paymentCardBrand || booking.customFields?.paymentCardBrand;
  const cardLast4 = booking.paymentCardLast4 || booking.customFields?.paymentCardLast4;
  const method = booking.paymentMethod || booking.customFields?.paymentMethod;

  let paymentText = booking.paymentMethodDisplay;
  if (!paymentText) {
    if (isPaid) {
      if (cardBrand && cardLast4) {
        const brandCap = String(cardBrand).charAt(0).toUpperCase() + String(cardBrand).slice(1);
        paymentText = `${brandCap} ****${cardLast4}`;
      } else if (method === 'promptpay') {
        paymentText = 'PromptPay';
      } else {
        paymentText = tDict.paid;
      }
    } else {
      if (isRfp) {
        paymentText = lang === 'th' ? 'ขอใบเสนอราคา (Quotation on Request)' : 'Quotation on Request';
      } else if (isSeminar) {
        paymentText = lang === 'th' ? 'ชำระ ณ จุดลงทะเบียน (Pay at Venue)' : 'Pay at Venue';
      } else if (isSpa) {
        paymentText = tDict.payAtSpa;
      } else if (isRestaurant) {
        paymentText = tDict.payAtRestaurant;
      } else if (isMice) {
        paymentText = totalAmount > 0 ? tDict.payAtVenue : tDict.quotationRequested;
      } else if (isGenericEngine) {
        paymentText = tDict.payAtVenue;
      } else {
        paymentText = tDict.payAtHotel;
      }
    }
  }

  // Dynamic contents for body
  const contentRows: any[] = [];

  if (isSpa) {
    const spa = booking.spaDetails || {};
    const spaOutletName = typeof spa.outletName === 'object'
      ? (spa.outletName[lang] || spa.outletName.en || spa.outletName.th || Object.values(spa.outletName)[0] || tDict.spaService)
      : (spa.outletName || tDict.spaService);
    const treatmentTitle = typeof spa.treatmentName === 'object'
      ? (spa.treatmentName[lang] || spa.treatmentName.en || spa.treatmentName.th || Object.values(spa.treatmentName)[0] || tDict.spaTreatment)
      : (spa.treatmentName || booking.roomTypeName || tDict.spaTreatment);
    const serviceDate = spa.bookingDate || booking.checkIn;
    const timeSlotStr = spa.timeSlot ? `${spa.timeSlot}${lang === 'th' ? ' น.' : ''}${spa.durationMinutes ? ` (${spa.durationMinutes} ${tDict.spaDuration})` : ''}` : '-';
    const paxCount = spa.pax || booking.adults || 1;

    const extraChargeTotal = booking.extraCharges?.totalExtraCharge || 0;
    const spaRateBeforeTax = (booking.totalBeforeTax && booking.totalBeforeTax < totalAmount)
      ? booking.totalBeforeTax
      : Math.round((totalAmount - extraChargeTotal) / 1.177);
    const svc = Math.round(spaRateBeforeTax * 0.10);
    const vat = Math.round((spaRateBeforeTax + svc) * 0.07);

    contentRows.push(
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🌿', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.spaService, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: spaOutletName, size: 'sm', color: '#7e22ce', weight: 'bold', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💆', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.spaTreatment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: treatmentTitle, size: 'sm', color: '#111111', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👤', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guest, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: guestName, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.serviceDate, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(serviceDate), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '⏰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.timeSlot, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: timeSlotStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👥', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guests, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: `${paxCount} ${tDict.adults}`, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(spa.roomNumber ? [{
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🚪', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.roomNumber, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: String(spa.roomNumber), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      }] : []),
      ...(totalAmount > 0
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '💵', size: 'sm', flex: 0 },
                { type: 'text', text: (tDict as any).serviceRateBeforeTax || tDict.roomRateBeforeTax, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(spaRateBeforeTax, currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
            ...(extraChargeTotal > 0
              ? [
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      { type: 'text', text: '➕', size: 'sm', flex: 0 },
                      { type: 'text', text: tDict.extraCharges, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                      { type: 'text', text: formatCurrency(extraChargeTotal, currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
                    ],
                    margin: 'md',
                  },
                ]
              : []),
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '📊', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.serviceCharge10, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(svc, currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '📊', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.vat7, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(vat, currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.total, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatCurrency(totalAmount, currency), size: 'lg', weight: 'bold', color: '#00B900', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💳', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.payment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: paymentText,
            size: 'sm',
            weight: 'bold',
            color: isPaid ? '#00B900' : '#0284c7',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      }
    );
  } else if (isRestaurant) {
    const rest = booking.restaurantDetails || {};
    const restaurantOutletName = typeof rest.outletName === 'object'
      ? (rest.outletName[lang] || rest.outletName.en || rest.outletName.th || Object.values(rest.outletName)[0] || tDict.diningOutlet)
      : (rest.outletName || tDict.diningOutlet);
    const serviceDate = rest.bookingDate || booking.checkIn;
    const timeSlotStr = rest.timeSlot ? `${rest.timeSlot}${lang === 'th' ? ' น.' : ''}` : '-';
    const partySizeCount = rest.partySize || booking.adults || 2;
    const seatingAreaStr = typeof rest.seatingArea === 'object'
      ? (rest.seatingArea[lang] || rest.seatingArea.en || rest.seatingArea.th || Object.values(rest.seatingArea)[0])
      : (rest.seatingArea === 'Indoor (แอร์)'
          ? (lang === 'en' ? 'Indoor (A/C)' : lang === 'zh' ? '室内 (空调)' : lang === 'ru' ? 'В помещении (кондиционер)' : lang === 'ja' ? '屋内 (エアコン)' : lang === 'ko' ? '실내 (에어컨)' : 'Indoor (แอร์)')
          : rest.seatingArea === 'Outdoor (ริมสระ)'
            ? (lang === 'en' ? 'Outdoor (Poolside)' : lang === 'zh' ? '室外 (泳池边)' : lang === 'ru' ? 'На открытом воздухе (у бассейна)' : lang === 'ja' ? '屋外 (プールサイド)' : lang === 'ko' ? '야외 (수영장 옆)' : 'Outdoor (ริมสระ)')
            : rest.seatingArea === 'VIP Room'
              ? (lang === 'en' ? 'VIP Room' : lang === 'zh' ? 'VIP 贵宾房' : lang === 'ru' ? 'VIP комната' : lang === 'ja' ? 'VIPルーム' : lang === 'ko' ? 'VIP 룸' : 'VIP Room')
              : (rest.seatingArea || 'Indoor'));
    const partyUnit = lang === 'th' ? 'ท่าน'
      : lang === 'zh' ? '位'
      : lang === 'ru' ? 'гостей'
      : lang === 'ja' ? '名'
      : lang === 'ko' ? '명'
      : 'Guests';

    contentRows.push(
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🍽️', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.diningOutlet, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: restaurantOutletName, size: 'sm', color: '#ea580c', weight: 'bold', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🪑', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.diningSeating, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: seatingAreaStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👤', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guest, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: guestName, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.serviceDate, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(serviceDate), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '⏰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.timeSlot, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: timeSlotStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👥', size: 'sm', flex: 0 },
          { type: 'text', text: lang === 'th' ? 'จำนวนที่นั่ง' : tDict.guests, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: `${partySizeCount} ${partyUnit}`, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(rest.roomNumber ? [{
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🚪', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.roomNumber, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: String(rest.roomNumber), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      }] : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💳', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.payment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: paymentText,
            size: 'sm',
            weight: 'bold',
            color: isPaid ? '#00B900' : '#0284c7',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      }
    );
  } else if (isMice) {
    const mice = booking.miceDetails || booking.customFields?.miceDetails || {};
    const functionRoomName = typeof mice.functionRoomName === 'object'
      ? (mice.functionRoomName[lang] || mice.functionRoomName.th || mice.functionRoomName.en || mice.functionRoomName.zh)
      : (mice.functionRoomName || booking.roomTypeName || (lang === 'th' ? 'ห้องประชุมสัมมนา' : lang === 'zh' ? '会议多功能厅' : 'Function & Meeting Room'));
    const eventDate = mice.eventDate || booking.checkIn;
    const timeSlotStr = mice.timeSlotName || mice.timeSlot || '08:30 - 17:00 (Full-Day)';
    const paxCount = mice.expectedPax || mice.pax || booking.adults || 20;
    const layoutStr = typeof mice.layoutStyle === 'string'
      ? (mice.layoutStyle.charAt(0).toUpperCase() + mice.layoutStyle.slice(1))
      : 'Classroom';
    const packageName = typeof mice.packageName === 'object'
      ? (mice.packageName[lang] || mice.packageName.th || mice.packageName.en || mice.packageName.zh)
      : (mice.packageName || booking.ratePlanName);
    const companyName = mice.companyName || booking.companyName || booking.customFields?.companyName;

    contentRows.push(
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🏛️', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.miceRoom, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: functionRoomName, size: 'sm', color: '#2563eb', weight: 'bold', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📐', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.miceLayout, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: layoutStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(packageName
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '☕', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.micePackage, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: packageName, size: 'sm', color: '#111111', align: 'end', flex: 4, wrap: true },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👤', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guest, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: guestName, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(companyName
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '🏢', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.miceCompany, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: companyName, size: 'sm', color: '#111111', align: 'end', flex: 4, wrap: true },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.serviceDate, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(eventDate), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '⏰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.timeSlot, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: timeSlotStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👥', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guests, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: `${paxCount} ${tDict.adults}`, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(totalAmount > 0
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '💰', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.total, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(totalAmount, currency), size: 'lg', weight: 'bold', color: '#00B900', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💳', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.payment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: paymentText,
            size: 'sm',
            weight: 'bold',
            color: isPaid ? '#00B900' : '#2563eb',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      }
    );
  } else if (isGenericEngine) {
    // Multi-Industry Generic Engine Booking (APPOINTMENT, ORDER, TICKET, COURSE, etc.)
    const resourceName = booking.resourceName || (typeof booking.spaDetails?.outletName === 'object' ? (booking.spaDetails?.outletName?.[lang] || booking.spaDetails?.outletName?.th || booking.spaDetails?.outletName?.en) : (booking.spaDetails?.outletName || booking.roomTypeName || '-'));
    const itemTitle = booking.itemName || booking.courseName || booking.treatmentName || booking.roomTypeName || (typeof booking.spaDetails?.treatmentName === 'object' ? (booking.spaDetails?.treatmentName?.[lang] || booking.spaDetails?.treatmentName?.th || booking.spaDetails?.treatmentName?.en) : booking.spaDetails?.treatmentName) || '-';
    const serviceDate = booking.checkIn || booking.spaDetails?.bookingDate || booking.startAt || '';
    const timeSlotStr = booking.timeSlot || booking.spaDetails?.timeSlot || '';
    const reqDetails = booking.customFields?.specialRequests || booking.customFields?.requirements || booking.miceDetails?.specialRequests || booking.specialRequests || '';

    const itemLabel = isSeminar
      ? (lang === 'th' ? 'หลักสูตร / หัวข้ออบรม' : lang === 'zh' ? '课程 / 培训主题' : lang === 'ru' ? 'Курс / Семинар' : lang === 'ja' ? '研修 / セミナー講座' : lang === 'ko' ? '교육 / 세미นา 과정' : 'Course / Topic')
      : isRfp
      ? (lang === 'th' ? 'ประเภทคำขอ / บริการ' : lang === 'zh' ? '申请类型 / 服务' : 'Request Type / Service')
      : isAppointment
      ? (lang === 'th' ? 'บริการ / นัดหมาย' : lang === 'zh' ? '服务 / 预约' : 'Service / Appointment')
      : (lang === 'th' ? 'บริการ' : lang === 'zh' ? '服务项目' : lang === 'ru' ? 'Услуга' : lang === 'ja' ? 'サービス' : lang === 'ko' ? '서비스' : 'Service');

    const itemIcon = isSeminar ? '🎓' : isRfp ? '📋' : '🏷️';

    contentRows.push(
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: itemIcon, size: 'sm', flex: 0 },
          { type: 'text', text: itemLabel, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: itemTitle, size: 'sm', color: '#111111', weight: 'bold', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      ...(resourceName && resourceName !== '-' ? [{
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📍', size: 'sm', flex: 0 },
          { type: 'text', text: lang === 'th' ? 'สถานที่ / สาขา' : lang === 'zh' ? '地点 / 分店' : lang === 'ru' ? 'Локация' : lang === 'ja' ? '場所' : lang === 'ko' ? '장소' : 'Location', size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: resourceName, size: 'sm', color: '#0284c7', weight: 'bold', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      }] : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👤', size: 'sm', flex: 0 },
          { type: 'text', text: lang === 'th' ? 'ชื่อผู้จอง / ผู้รับบริการ' : lang === 'zh' ? '客户姓名' : lang === 'ru' ? 'Клиент' : lang === 'ja' ? 'お客様氏名' : lang === 'ko' ? '예약자 성명' : 'Customer Name', size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: guestName, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(serviceDate ? [{
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.serviceDate, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(serviceDate), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      }] : []),
      ...(timeSlotStr ? [{
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '⏰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.timeSlot, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: timeSlotStr, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      }] : []),
      ...(reqDetails ? [{
        type: 'box',
        layout: 'vertical',
        backgroundColor: '#f8fafc',
        cornerRadius: 'md',
        paddingAll: 'sm',
        margin: 'md',
        contents: [
          {
            type: 'text',
            text: lang === 'th' ? '📝 รายละเอียดความต้องการ / โครงการ:' : '📝 Project / Requirements Details:',
            size: 'xs',
            color: '#475569',
            weight: 'bold',
          },
          {
            type: 'text',
            text: String(reqDetails),
            size: 'sm',
            color: '#0f172a',
            wrap: true,
            margin: 'xs',
          },
        ],
      }] : []),
      ...(totalAmount > 0
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '💰', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.total, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(totalAmount, currency), size: 'lg', weight: 'bold', color: '#00B900', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💳', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.payment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: paymentText,
            size: 'sm',
            weight: 'bold',
            color: isPaid ? '#00B900' : '#2563eb',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      }
    );
  } else {
    // Room Booking
    const checkInD = new Date(booking.checkIn);
    const checkOutD = new Date(booking.checkOut);
    const nights = booking.nights || (Number.isNaN(checkInD.getTime()) || Number.isNaN(checkOutD.getTime()) ? 1 : Math.max(1, Math.round((checkOutD.getTime() - checkInD.getTime()) / 86400000)));

    const extraChargeTotal = booking.extraCharges?.totalExtraCharge || 0;
    const roomRateBeforeTax = booking.totalBeforeTax || Math.round((totalAmount - extraChargeTotal) / 1.177);
    const svc = Math.round(roomRateBeforeTax * 0.10);
    const vat = Math.round((roomRateBeforeTax + svc) * 0.07);

    contentRows.push(
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🏨', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.roomType, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: booking.roomTypeName || '-', size: 'sm', color: '#111111', align: 'end', flex: 4, wrap: true },
        ],
        margin: 'md',
      },
      ...(booking.ratePlanName
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '📋', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.ratePlan, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: booking.ratePlanName, size: 'sm', color: '#111111', align: 'end', flex: 4, wrap: true },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: booking.isRefundable === false ? '⛔' : '✅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.refundPolicy, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: booking.isRefundable === false ? tDict.nonRefundable : tDict.refundable,
            size: 'sm',
            weight: 'bold',
            color: booking.isRefundable === false ? '#D9381E' : '#00B900',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👤', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guest, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: guestName, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.checkIn, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(booking.checkIn), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📅', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.checkOut, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatDate(booking.checkOut), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🌙', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.nights, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: `${nights} ${tDict.nights}`, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '🚪', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.rooms, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: `${booking.rooms || 1} ${tDict.rooms}`, size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '👥', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.guests, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: `${booking.adults || 2} ${tDict.adults}${booking.children && booking.children > 0 ? `, ${booking.children} ${tDict.children}` : ''}`,
            size: 'sm',
            color: '#111111',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💵', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.roomRateBeforeTax, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatCurrency(roomRateBeforeTax, booking.currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      ...(extraChargeTotal > 0
        ? [
            {
              type: 'box',
              layout: 'baseline',
              contents: [
                { type: 'text', text: '➕', size: 'sm', flex: 0 },
                { type: 'text', text: tDict.extraCharges, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
                { type: 'text', text: formatCurrency(extraChargeTotal, booking.currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
              ],
              margin: 'md',
            },
          ]
        : []),
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📊', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.serviceCharge10, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatCurrency(svc, booking.currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '📊', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.vat7, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatCurrency(vat, booking.currency), size: 'sm', color: '#111111', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💰', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.total, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          { type: 'text', text: formatCurrency(totalAmount, booking.currency), size: 'lg', weight: 'bold', color: '#00B900', align: 'end', flex: 4 },
        ],
        margin: 'md',
      },
      {
        type: 'box',
        layout: 'baseline',
        contents: [
          { type: 'text', text: '💳', size: 'sm', flex: 0 },
          { type: 'text', text: tDict.payment, size: 'sm', color: '#666666', flex: 3, margin: 'sm' },
          {
            type: 'text',
            text: paymentText,
            size: 'sm',
            weight: 'bold',
            color: isPaid ? '#00B900' : '#0284c7',
            align: 'end',
            flex: 4,
          },
        ],
        margin: 'md',
      }
    );
  }

  // Dynamic footer notice
  const footerContents: any[] = [];
  if (isSpa) {
    footerContents.push({
      type: 'text',
      text: `🕐 ${tDict.spaNotice}`,
      size: 'xs',
      color: '#666666',
      margin: 'sm',
    });
  } else if (isRestaurant) {
    footerContents.push({
      type: 'text',
      text: `🕐 ${tDict.diningNotice}`,
      size: 'xs',
      color: '#666666',
      margin: 'sm',
    });
  } else if (isMice) {
    footerContents.push({
      type: 'text',
      text: `🏛️ ${tDict.miceNotice}`,
      size: 'xs',
      color: '#666666',
      margin: 'sm',
      wrap: true,
    });
  } else if (isRfp) {
    footerContents.push({
      type: 'text',
      text: lang === 'th' ? '📄 ได้รับคำขอแล้ว ทางทีมงานจะติดต่อกลับพร้อมใบเสนอราคาโดยเร็ว' : '📄 Request received. Our team will contact you shortly.',
      size: 'xs',
      color: '#666666',
      margin: 'sm',
      wrap: true,
    });
  } else if (isSeminar) {
    footerContents.push(
      {
        type: 'text',
        text: `🕐 ${tDict.seminarNotice || 'กรุณามาถึงก่อนเวลาเริ่มสัมมนา 15-30 นาที เพื่อลงทะเบียนรับเอกสาร'}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
        wrap: true,
      },
      {
        type: 'text',
        text: `📍 ${tDict.seminarDeskNotice || 'กรุณานำหมายเลขยืนยันนี้แสดง ณ จุดลงทะเบียน'}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
        wrap: true,
      }
    );
  } else if (isGenericEngine) {
    footerContents.push(
      {
        type: 'text',
        text: `🕐 ${tDict.genericNotice || 'กรุณามาถึงก่อนเวลานัดหมาย 10-15 นาที'}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
        wrap: true,
      },
      {
        type: 'text',
        text: `📍 ${tDict.genericDeskNotice || 'กรุณาแสดงหมายเลขยืนยันนี้เมื่อเข้ารับบริการ'}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
        wrap: true,
      }
    );
  } else {
    footerContents.push(
      {
        type: 'text',
        text: `🕐 ${tDict.checkInTime}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
      },
      {
        type: 'text',
        text: `🕐 ${tDict.checkOutTime}`,
        size: 'xs',
        color: '#666666',
        margin: 'sm',
      }
    );
  }

  const headerBgColor = isMice ? '#1e40af' : isSpa ? '#7e22ce' : isRestaurant ? '#ea580c' : '#06C755';
  const confNumberColor = isMice ? '#2563eb' : isSpa ? '#7e22ce' : isRestaurant ? '#ea580c' : '#00B900';
  const confNumberBg = isMice ? '#eff6ff' : isSpa ? '#faf5ff' : isRestaurant ? '#fff7ed' : '#E8F5E9';

  return {
    type: 'flex',
    altText: `✅ ${cardTitle} - ${booking.confirmationNumber}`,
    contents: {
      type: 'bubble',
      styles: {
        header: {
          backgroundColor: headerBgColor,
        },
      },
      header: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'text',
            text: cardTitle,
            weight: 'bold',
            size: 'xl',
            color: '#ffffff',
            align: 'center',
          },
          {
            type: 'text',
            text: cardSubtitle,
            size: 'sm',
            color: '#ffffff',
            align: 'center',
            margin: 'xs',
          },
        ],
        paddingAll: 'lg',
        alignItems: 'center',
      },
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: tDict.confNum,
                size: 'sm',
                color: '#999999',
                align: 'center',
              },
              {
                type: 'text',
                text: booking.confirmationNumber || booking.id || '',
                size: 'xl',
                weight: 'bold',
                color: confNumberColor,
                align: 'center',
                margin: 'xs',
              },
            ],
            backgroundColor: confNumberBg,
            cornerRadius: 'md',
            paddingAll: 'md',
            margin: 'none',
          },
          {
            type: 'separator',
            margin: 'lg',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: contentRows,
            margin: 'md',
          },
          {
            type: 'separator',
            margin: 'xl',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: footerContents,
            margin: 'md',
          },
        ],
        paddingAll: 'lg',
      },
    },
  };
}

/**
 * Initialize success page event handlers
 */
export function initSuccessPage(): void {
  const state = getState();
  const booking = state.booking;
  const confNum = booking?.confirmationNumber || booking?.id || '';
  const storageKey = `sent_flex_${confNum}`;
  const alreadySent = !confNum || sentConfirmationNumbers.has(confNum) || sessionStorage.getItem(storageKey) === '1';
  const channel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || 'line';
  const isLine = channel === 'line';

  if (booking && !alreadySent && isLine) {
    sentConfirmationNumbers.add(confNum);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch (_) {}

    // For LINE channel: send LIFF Flex message directly into chat
    try {
      const flexMessage = buildSuccessFlexMessage(booking);
      sendMessage(flexMessage).catch((err) => {
        console.warn('[Success] Auto-send in-chat confirmation note:', err);
      });
    } catch (e) {
      console.warn('[Success] Error preparing auto-send message:', e);
    }
  }

  // Helper to close WeChat In-App Browser window
  const closeWeChatWindow = () => {
    try {
      if (typeof (window as any).WeixinJSBridge !== 'undefined') {
        (window as any).WeixinJSBridge.call('closeWindow');
      } else {
        document.addEventListener(
          'WeixinJSBridgeReady',
          () => {
            (window as any).WeixinJSBridge.call('closeWindow');
          },
          false
        );
      }
    } catch (_) {}
  };

  // Navigation handlers
  window.closeSuccessPage = () => {
    if (channel === 'wechat') {
      closeWeChatWindow();
      try { window.close(); } catch (_) {}
      try { window.history.back(); } catch (_) {}
      return;
    }

    if (isLine) {
      try { closeWindow(); } catch (_) {}
      try { window.close(); } catch (_) {}
      return;
    }

    // Fallback for web / whatsapp
    try { window.close(); } catch (_) {}
    navigateTo('service');
  };

  (window as any).goToServicesOrHome = () => {
    navigateTo('service');
  };

  // Automatically close window after 2.5 seconds in WeChat or 1.5 seconds in LINE
  if (channel === 'wechat') {
    setTimeout(() => {
      closeWeChatWindow();
    }, 2500);
  } else if (isLine) {
    setTimeout(() => {
      try {
        closeWindow();
      } catch (e) {
        console.warn('[Success] Auto-close failed (likely external browser):', e);
      }
    }, 1500);
  }
}

// Extend window for global handlers
declare global {
  interface Window {
    closeSuccessPage: () => void;
    goToServicesOrHome?: () => void;
  }
}
